/**
 * Custom RouteReuseStrategy for Angular routing.
 *
 * This strategy ensures that any route whose full path contains 'consentmanagement'
 * is *not* reused, but instead fully destroyed and recreated upon navigation.
 * This guarantees that ngOnDestroy is called and the component is reinitialized.
 *
 * All other routes follow Angular's default reuse behavior (reuse if routeConfig matches).
 *
 * 📎 Created with the help of ChatGPT – https://chat.openai.com/share/739e8f26-bc66-476f-b204-4d5f6d0f2b0a
 * 🧠 Chat log includes explanation and integration details.
 */

import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

export class ConsentManagementReuseStrategy implements RouteReuseStrategy {
  /**
   * Don't allow detaching routes from the DOM (we don't want to store them for reuse)
   */
  shouldDetach(): boolean {
    return false;
  }

  /**
   * No-op: we’re not storing any routes
   */
  store(): void {
    // Intentionally left empty — route reuse is disabled
  }

  /**
   * Never try to reattach stored routes
   */
  shouldAttach(): boolean {
    return false;
  }

  /**
   * No route stored = always null
   */
  retrieve(): DetachedRouteHandle | null {
    return null;
  }

  /**
   * Custom reuse logic: only reuse route if it does *not* contain 'consentmanagement'
   */
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    const futurePath = this.getFullRoutePath(future);
    const currPath = this.getFullRoutePath(curr);

    const isConsentRoute = (path: string) => path.includes('consentmanagement');

    if (isConsentRoute(futurePath) || isConsentRoute(currPath)) {
      // 🚫 Disable reuse for routes involving consent management
      return false;
    }

    // ✅ Reuse by default if route config matches
    return future.routeConfig === curr.routeConfig;
  }

  /**
   * Helper to build full route path from root to current route
   */
  private getFullRoutePath(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
      .map((v) => v.routeConfig?.path)
      .filter(Boolean)
      .join('/');
  }
}
